import React, { useState } from 'react';
import styled from 'astroturf/react';
import moment from 'moment';
import pluralize from 'pluralize';
import { TruncateText } from 'js/liswp/TruncateText';
import { Avatar, Card, Header, Institution } from './common';
import { User } from './hooks/types';

export type Recommendation = {
  // A user can leave a recommendation without a comment
  comment?: string;
  // DateTime the user left the recommendation
  date: string;
  user: User;
};

type Props = {
  // Whether to use the alternate clean look (part of swp_clean_rr ab test)
  altLook?: boolean;
  recommendations: Recommendation[] | undefined;
  userCertWorkId: number | undefined;
  userCertComment: string | undefined;
};

const AvatarGroup = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

const Collapser = styled.p`
  background: #fff;
  border-bottom: 1px solid #ddd;
  color: #0954d3;
  cursor: pointer;
  font-weight: 500;
  margin: 0;
  padding: 8px 12px;

  &.altLook {
    border-bottom: 0;
    font-weight: 400;
    text-transform: none;
    padding-bottom: 2px;
  }
`;

const Comment = styled.p`
  color: #333;
  font-size: 13px;
  margin: 0;
`;

const Container = styled.div`
  background: #fff;
  border-bottom: 1px solid #ddd;
  padding: 12px;
  padding-bottom: 4px;

  &.altLook {
    border-bottom: 1px solid transparent;
    position: relative;
  }

  // With altLook, there's a border bottom that doesn't go all the way to the left edge.
  // We can't use a left-margin because we want the hover effect to take
  // up the entire space, so we have to use an after element for the bottom border.
  &.altLook:not(:last-of-type)::after {
    background: #ddd;
    bottom: 0;
    content: '';
    left: 12px;
    height: 1px;
    position: absolute;
    width: calc(100% - 12px);
  }

  &.altLook:last-of-type {
    border-bottom: 0;
  }
`;

const DateTime = styled.p`
  color: #999;
  font-size: 12px;
  margin: 0;

  &::before {
    content: '\\2022';
    margin: 0 4px;
  }

  &.altLook {
    color: #888;
  }
`;

const Name = styled.h2`
  color: #4b4b4b;
  font-size: 14px;
  margin: 0 8px 0 0;

  &.altLook {
    margin-right: 7px;
  }
`;

const NameGroup = styled.div`
  align-items: baseline;
  display: flex;
`;

const RecommendationsCard = ({ altLook, recommendations }: Props) => {
  const [collapsed, setCollapsed] = useState(true);
  let recs = recommendations;

  if (!recs || recs.length === 0) {
    return null;
  }
  const numRecs = recs.length;
  if (collapsed) {
    recs = recs.slice(0, 1);
  }

  const showCollapser = numRecs > 1;

  const collapser = (
    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
    <Collapser altLook={altLook} onClick={() => setCollapsed((c) => !c)}>
      {collapsed ? 'Show All ▾' : 'Show Less'}
    </Collapser>
  );

  return (
    <Card altLook={altLook}>
      <Header altLook={altLook}>
        {numRecs} {pluralize('Recommendation', numRecs)} of this paper
        {altLook && showCollapser && collapser}
      </Header>

      {recs.map((rec, i) => (
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        <Container altLook={altLook} key={i}>
          <AvatarGroup>
            <Avatar altLook={altLook} small src={rec.user.photo} />

            <div>
              <NameGroup>
                {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
                <Name altLook={altLook}>{rec.user.displayName}</Name>
                {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
                <DateTime altLook={altLook}>
                  {moment(rec.date).format('MMMM Do, YYYY')}
                </DateTime>
              </NameGroup>

              {rec.user.university && rec.user.position && (
                <Institution altLook={altLook} small>
                  {rec.user.university}, {rec.user.position}
                </Institution>
              )}
            </div>
          </AvatarGroup>

          {rec.comment && (
            <Comment>
              <TruncateText text={rec.comment} characters={150} collapsible />
            </Comment>
          )}
        </Container>
      ))}
      {!altLook && showCollapser && collapser}
    </Card>
  );
};

export default RecommendationsCard;
